import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, XCircle, Calendar, MapPin, Loader2, Image as ImageIcon, Upload } from 'lucide-react';
import api from '../../utils/axios';
import getFullUrl from '../../utils/formatUrl';

const EventManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    description: '',
    image: '',
    category: 'Event',
    isActive: true,
    
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await api.get('/events');
      setEvents(res.data.data);
    } catch (err) {
      console.error('Failed to fetch events', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const res = await api.post('/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image: res.data.url }));
    } catch (err) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/events/${editingId}`, formData);
      } else {
        await api.post('/events', formData);
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', date: '', location: '', description: '', image: '', category: 'Event', isActive: true });
      fetchEvents();
    } catch (err) {
      alert('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title,
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : '',
      location: event.location || '',
      description: event.description || '',
      image: event.image || '',
      category: event.category || 'Event',
      isActive: event.isActive,
    });
    setEditingId(event._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await api.delete(`/events/${id}`);
        setEvents(events.filter(e => e._id !== id));
      } catch (err) {
        alert('Failed to delete');
      }
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', date: '', location: '', description: '', image: '', category: 'Event', isActive: true });
  };

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-8 rounded-sm relative overflow-hidden">
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic flex items-center">
              <Calendar size={28} className="mr-3" /> Event Management
            </h4>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 max-w-xl">
              Create and manage institutional events, seminars, and workshops.
            </p>
          </div>
          <button
            onClick={() => { cancelForm(); setShowForm(true); }}
            className="bg-[#cc0000] text-white px-6 py-3 rounded-sm font-black uppercase text-xs tracking-widest flex items-center hover:bg-white hover:text-[#cc0000] transition-all shadow-xl"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Event
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 animate-scaleIn">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-black uppercase tracking-tighter text-slate-900">
              {editingId ? 'Edit Event' : 'Add New Event'}
            </h3>
            <button onClick={cancelForm} className="text-slate-400 hover:text-red-500">
              <XCircle size={22} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Event Title</label>
                <input
                  type="text"
                  required
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Date</label>
                <input
                  type="date"
                  required
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Location</label>
                <input
                  type="text"
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm"
                  value={formData.location}
                  onChange={e => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Campus / Venue"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Category</label>
                <select
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-transparent"
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="Event">Institutional Event</option>
                  <option value="News">Latest News</option>
                  <option value="Notice">Official Notice</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 accent-[#cc0000]"
              />
              <label htmlFor="isActive" className="text-sm font-bold text-slate-700">Active (visible on website)</label>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Event Image</label>
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-slate-100 border border-slate-200 rounded-sm overflow-hidden flex items-center justify-center">
                  {formData.image ? (
                    <img src={getFullUrl(formData.image)} className="w-full h-full object-cover" alt="Event" />
                  ) : (
                    <ImageIcon size={24} className="text-slate-300" />
                  )}
                </div>
                <label className={`cursor-pointer px-4 py-2 bg-slate-900 text-white rounded-sm text-[10px] font-black uppercase hover:bg-[#cc0000] transition-all flex items-center ${uploading ? 'opacity-50' : ''}`}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload Image'}
                  <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                </label>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Description</label>
              <textarea
                rows="4"
                className="w-full border border-slate-100 bg-slate-50 p-4 rounded-sm outline-none focus:border-[#cc0000] text-sm font-semibold"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="bg-[#cc0000] text-white px-10 py-4 font-black uppercase text-xs tracking-[3px] shadow-xl hover:bg-slate-900 transition-all disabled:opacity-50 flex items-center"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Save'} Event</>}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="bg-slate-100 text-slate-600 px-8 py-4 font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-600">
            All Events ({events.length})
          </h3>
        </div>

        {loading ? (
          <div className="p-20 text-center">
            <Loader2 size={32} className="animate-spin text-slate-300 mx-auto" />
            <p className="text-slate-400 font-bold text-xs mt-4 uppercase tracking-widest">Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="p-20 text-center">
            <Calendar size={48} className="text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-bold text-sm">No events added yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {events.map((event) => (
              <div key={event._id} className="p-6 flex items-start justify-between hover:bg-slate-50/50 transition-colors group">
                <div className="flex gap-6 items-start">
                  <div className="w-24 h-24 bg-slate-100 border border-slate-200 rounded-sm overflow-hidden flex-shrink-0">
                    <img src={getFullUrl(event.image)} className="w-full h-full object-cover" alt="" />
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-slate-900 uppercase tracking-tight">{event.title}</h4>
                    <div className="flex items-center gap-4 mt-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                      <span className={`px-2 py-0.5 rounded-sm font-black text-[8px] tracking-tighter ${
                        event.category === 'News' ? 'bg-blue-600 text-white' : 
                        event.category === 'Notice' ? 'bg-amber-500 text-white' : 
                        'bg-slate-900 text-white'
                      }`}>
                        {event.category || 'Event'}
                      </span>
                      <span className="flex items-center"><Calendar size={12} className="mr-1 text-[#cc0000]" /> {new Date(event.date).toLocaleDateString()}</span>
                      {event.location && <span className="flex items-center"><MapPin size={12} className="mr-1 text-[#cc0000]" /> {event.location}</span>}
                      <span className={`px-2 py-0.5 rounded-full ${event.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                        {event.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2 max-w-2xl">{event.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit(event)}
                    className="bg-slate-100 text-slate-600 px-3 py-2 rounded-sm text-[10px] font-black uppercase tracking-wider hover:bg-[#003366] hover:text-white transition-all"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(event._id)}
                    className="bg-red-50 text-red-500 px-3 py-2 rounded-sm hover:bg-red-500 hover:text-white transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManager;
